import { Route } from "@angular/router";
import { initialDataResolver } from "app/app.resolvers";
import { AuthGuard } from "app/core/auth/guards/auth.guard";
import { NoAuthGuard } from "app/core/auth/guards/noAuth.guard";
import { LayoutComponent } from "app/layout/layout.component";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
  // Redirect empty path to '/example'
  { path: "", pathMatch: "full", redirectTo: "dashboard" },

  // Redirect signed-in user to the '/example'
  //
  // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
  // path. Below is another redirection for that path to redirect the user to the desired
  // location. This is a small convenience to keep all main routes together here on this file.
  { path: "signed-in-redirect", pathMatch: "full", redirectTo: "dashboard" },

  // Auth routes for guests
  {
    path: "",
    canActivate: [NoAuthGuard],
    canActivateChild: [NoAuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "confirmation-required",
        loadChildren: () =>
          import(
            "app/modules/auth/confirmation-required/confirmation-required.routes"
          ),
      },
      {
        path: "forgot-password",
        loadChildren: () =>
          import("app/modules/auth/forgot-password/forgot-password.routes"),
      },
      {
        path: "reset-password",
        loadChildren: () =>
          import("app/modules/auth/reset-password/reset-password.routes"),
      },
      {
        path: "sign-in",
        loadChildren: () => import("app/modules/auth/sign-in/sign-in.routes"),
      },
      {
        path: "sign-up",
        loadChildren: () => import("app/modules/auth/sign-up/sign-up.routes"),
      },
      {
        path: "terms",
        loadChildren: () => import("app/modules/auth/terms/terms.routes"),
      },
      {
        path: "privacy",
        loadChildren: () => import("app/modules/auth/privacy/privacy.routes"),
      },
    ],
  },

  // Auth routes for authenticated users
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "sign-out",
        loadChildren: () => import("app/modules/auth/sign-out/sign-out.routes"),
      },
      {
        path: "unlock-session",
        loadChildren: () =>
          import("app/modules/auth/unlock-session/unlock-session.routes"),
      },
    ],
  },

  // Landing routes
  {
    path: "",
    component: LayoutComponent,
    data: {
      layout: "empty",
    },
    children: [
      {
        path: "home",
        loadChildren: () => import("app/modules/landing/home/home.routes"),
      },
    ],
  },

  // Admin routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "dashboard",
        loadChildren: () =>
          import("app/modules/admin/dashboard/dashboard.routes"),
      },
      {
        path: "categories",
        loadChildren: () =>
          import("app/modules/admin/categories/categories.routes"),
      },
      {
        path: "cursos",
        loadChildren: () =>
          import("app/modules/admin/cursos/cursos.routes"),
      },
      {
        path: "clases",
        loadChildren: () =>
          import("app/modules/admin/clases/clases.routes"),
      },
      {
        path: "subcategories",
        loadChildren: () =>
          import("app/modules/admin/subcategories/subcategories.routes"),
      },
      {
        path: "usersettings",
        loadChildren: () =>
          import("app/modules/admin/usersettings/usersettings.routes"),
      },
      {
        path: "users",
        loadChildren: () => import("app/modules/admin/users/users.routes"),
      },
      {
        path: "locations",
        loadChildren: () => import("app/modules/admin/locations/locations.routes"),
      },
    ],
  },

  // user routes
  {
    path: "",
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    component: LayoutComponent,
    resolve: {
      initialData: initialDataResolver,
    },
    children: [
      {
        path: "user/dashboard",
        loadChildren: () =>
          import("app/modules/user/dashboard/dashboard.routes"),
      },
      {
        path: "user/taxyears",
        loadChildren: () => import("app/modules/user/taxyears/taxyears.routes"),
      },
      {
        path: "user/taxmonths",
        loadChildren: () =>
          import("app/modules/user/taxmonths/taxmonths.routes"),
      },
      {
        path: "user/monthdashboard",
        loadChildren: () =>
          import("app/modules/user/monthdashboard/monthdashboard.routes"),
      },
      {
        path: "user/dailyincome",
        loadChildren: () =>
          import("app/modules/user/dailyincome/dailyincome.routes"),
      },
      {
        path: "user/dailyincomeregister",
        loadChildren: () =>
          import("app/modules/user/dailyincomeregister/dailyincomeregister.routes"),
      },
      {
        path: "user/dailyoperationalexpenses",
        loadChildren: () =>
          import(
            "app/modules/user/dailyoperationalexpenses/dailyoperationalexpenses.routes"
          ),
      },
      {
        path: "user/dailyusersubcategory",
        loadChildren: () =>
          import(
            "app/modules/user/dailyusersubcategory/dailyusersubcategory.routes"
          ),
      },
      {
        path: "user/purchasedproducts",
        loadChildren: () =>
          import("app/modules/user/purchasedproducts/purchasedproducts.routes"),
      },
      {
        path: "user/dailypurchasedproducts",
        loadChildren: () =>
          import(
            "app/modules/user/dailypurchasedproducts/dailypurchasedproducts.routes"
          ),
      },
      {
        path: "user/workerspayment",
        loadChildren: () =>
          import("app/modules/user/workerspayment/workerspayment.routes"),
      },
      {
        path: "user/dailyworkerspayments",
        loadChildren: () =>
          import(
            "app/modules/user/dailyworkerspayments/dailyworkerspayments.routes"
          ),
      },
      {
        path: "user/profitsandlossesmonth",
        loadChildren: () =>
          import(
            "app/modules/user/profitsandlossesmonth/profitsandlossesmonth.routes"
          ),
      },
      {
        path: "user/profitsandlossesyear",
        loadChildren: () =>
          import(
            "app/modules/user/profitsandlossesyear/profitsandlossesyear.routes"
          ),
      },
      {
        path: "user/operationalexpenses",
        loadChildren: () =>
          import(
            "app/modules/user/operationalexpenses/operationalexpenses.routes"
          ),
      },
      {
        path: "user/monthlymileage",
        loadChildren: () =>
          import("app/modules/user/monthlymileage/monthlymileage.routes"),
      },
      {
        path: "user/annualtotal",
        loadChildren: () =>
          import("app/modules/user/annualtotal/annualtotal.routes"),
      },
      {
        path: "user/help-center",
        loadChildren: () =>
          import("app/modules/user/help-center/help-center.routes"),
      },
      {
        path: "user/help-center/faqs",
        loadChildren: () =>
          import("app/modules/user/help-center/faqs/help-center-faqs.routes"),
      },
      {
        path: "user/help-center/support",
        loadChildren: () =>
          import(
            "app/modules/user/help-center/support/help-center-support.routes"
          ),
      },
      {
        path: "user/pricing",
        loadChildren: () => import("app/modules/user/pricing/pricing.routes"),
      },
      {
        path: "user/purchaseconfirmation",
        loadChildren: () =>
          import(
            "app/modules/user/purchaseconfirmation/purchaseconfirmation.routes"
          ),
      },
      {
        path: "user/locationpurchaseconfirmation",
        loadChildren: () =>
          import(
            "app/modules/user/locationpurchaseconfirmation/locationpurchaseconfirmation.routes"
          ),
      },
      {
        path: "user/purchaseerror",
        loadChildren: () =>
          import("app/modules/user/purchaseerror/purchaseerror.routes"),
      },
      {
        path: "user/workers",
        loadChildren: () => import("app/modules/user/workers/workers.routes"),
      },
      {
        path: "user/providers",
        loadChildren: () =>
          import("app/modules/user/providers/providers.routes"),
      },
      {
        path: "user/bills",
        loadChildren: () =>
          import("app/modules/user/bills/bills.routes"),
      },
      {
        path: "user/dataimport",
        loadChildren: () =>
          import("app/modules/user/dataimport/dataimport.routes"),
      },
      {
        path: "user/reports",
        loadChildren: () =>
          import("app/modules/user/reports/reports.routes"),
      },
      {
        path: "user/detailed-reports",
        loadChildren: () =>
          import("app/modules/user/detailed-reports/detailed-reports.routes"),
      },
      {
        path: "user/products",
        loadChildren: () =>
          import("app/modules/user/products/products.routes"),
      },
      {
        path: "user/clients",
        loadChildren: () => import("app/modules/user/clients/clients.routes"),
      },
      {
        path: "user/cursos",
        loadChildren: () => import("app/modules/user/academy/academy.routes"),
      },
      {
        path: "user/vehicles",
        loadChildren: () => import("app/modules/user/vehicles/vehicles.routes"),
      },
      {
        path: "user/bankaccounts",
        loadChildren: () =>
          import("app/modules/user/bankaccounts/bankaccounts.routes"),
      },
      {
        path: "user/loans",
        loadChildren: () => import("app/modules/user/loans/loans.routes"),
      },
      {
        path: "user/balancesheet",
        loadChildren: () =>
          import("app/modules/user/balancesheet/balancesheet.routes"),
      },
      {
        path: "user/balancesheetmonth",
        loadChildren: () =>
          import("app/modules/user/balancesheetmonth/balancesheetmonth.routes"),
      },
      {
        path: "user/dailysupplierspayments",
        loadChildren: () =>
          import(
            "app/modules/user/dailysupplierspayments/dailysupplierspayments.routes"
          ),
      },
      {
        path: "user/categories",
        loadChildren: () =>
          import("app/modules/admin/categories/categories.routes"),
      },
      {
        path: "user/subcategories",
        loadChildren: () =>
          import("app/modules/admin/subcategories/subcategories.routes"),
      },
    ],
  },
];
